import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  HR_CASE_CONFIDENTIALITY_LEVELS,
  HR_CASE_SEVERITIES,
  HR_CASE_STATUSES,
  HR_CASE_TYPES,
  HR_DISCIPLINARY_ACTION_STATUSES,
  HR_DISCIPLINARY_ACTION_TYPES,
  HrCase,
  HrCaseAccessGrant,
  HrCaseActivity,
  HrCaseAttachment,
  HrCaseDashboard,
  HrDisciplinaryAction,
  HrEmployee
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-hr-cases',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-cases.component.html',
  styleUrls: ['./hr-cases.component.scss']
})
export class HrCasesComponent implements OnInit {
  activeListTab = 'cases';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.cases.manage);
  readonly canManageDisciplinary = this.permissionService.hasPermission(app_permissions.hr.cases.disciplinaryManage);
  readonly canManageAccess = this.permissionService.hasPermission(app_permissions.hr.cases.accessManage);
  readonly caseTypeOptions = HR_CASE_TYPES.map(value => ({ label: this.label(value), value }));
  readonly caseStatusOptions = HR_CASE_STATUSES.map(value => ({ label: this.label(value), value }));
  readonly severityOptions = HR_CASE_SEVERITIES.map(value => ({ label: this.label(value), value }));
  readonly confidentialityOptions = HR_CASE_CONFIDENTIALITY_LEVELS.map(value => ({ label: this.label(value), value }));
  readonly actionTypeOptions = HR_DISCIPLINARY_ACTION_TYPES.map(value => ({ label: this.label(value), value }));
  readonly actionStatusOptions = HR_DISCIPLINARY_ACTION_STATUSES.map(value => ({ label: this.label(value), value }));

  dashboard?: HrCaseDashboard;
  employees: HrEmployee[] = [];
  cases: HrCase[] = [];
  activities: HrCaseActivity[] = [];
  attachments: HrCaseAttachment[] = [];
  disciplinaryActions: HrDisciplinaryAction[] = [];
  accessGrants: HrCaseAccessGrant[] = [];
  selectedCase?: HrCase;
  activeDialog?: 'case' | 'activity' | 'disciplinary' | 'attachment' | 'access';

  caseForm: HrCase = this.newCase();
  activityForm: HrCaseActivity = this.newActivity();
  attachmentForm: HrCaseAttachment = this.newAttachment();
  disciplinaryForm: HrDisciplinaryAction = this.newDisciplinaryAction();
  grantForm: HrCaseAccessGrant = this.newGrant();

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Total Cases', value: this.dashboard?.totalCases || 0, tone: 'info' },
      { label: 'Open Cases', value: this.dashboard?.openCases || 0, tone: 'warn' },
      { label: 'Closed Cases', value: this.dashboard?.closedCases || 0, tone: 'success' },
      { label: 'High Severity', value: this.dashboard?.highSeverityCases || 0, tone: 'danger' },
      { label: 'Acknowledgements', value: this.dashboard?.pendingAcknowledgements || 0, tone: 'warn' }
    ];
  }

  get employeeOptions(): SelectOption<number>[] {
    return this.employees.map(employee => ({
      label: `${employee.displayName || 'Employee'}${employee.employeeNumber ? ` - ${employee.employeeNumber}` : ''}`,
      value: employee.employeeId as number
    }));
  }

  get caseOptions(): SelectOption<number>[] {
    return this.cases.map(row => ({ label: `${row.caseCode || row.caseId} - ${row.caseTitle}`, value: row.caseId as number }));
  }

  load(): void {
    forkJoin({
      dashboard: this.hrService.caseDashboard(),
      cases: this.hrService.cases(undefined, undefined, undefined, 100),
      disciplinary: this.hrService.disciplinaryActions(),
      employees: this.hrService.employees({ pageSize: 300 })
    }).subscribe({
      next: result => {
        this.dashboard = result.dashboard.data;
        this.cases = result.cases.data || [];
        this.disciplinaryActions = result.disciplinary.data || [];
        this.employees = result.employees.items || [];
        if (!this.selectedCase && this.cases.length > 0) {
          this.selectCase(this.cases[0]);
        }
      },
      error: () => this.toast('error', 'Unable to load HR cases workspace.')
    });
  }

  saveCase(): void {
    this.hrService.saveCase(this.caseForm).subscribe({
      next: response => {
        this.activeDialog = undefined;
        this.caseForm = this.newCase();
        this.toast('success', response.message || 'HR case saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save HR case.')
    });
  }

  editCase(row: HrCase): void {
    this.caseForm = { ...row };
    this.selectCase(row);
    this.activeDialog = 'case';
  }

  closeCase(row: HrCase): void {
    if (!row.caseId) {
      return;
    }
    this.hrService.updateCaseStatus(row.caseId, { caseStatus: 'CLOSED', resolution: row.resolution || 'Closed from HR cases workspace.' }).subscribe({
      next: () => {
        this.toast('success', 'HR case closed.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to close HR case.')
    });
  }

  sealCase(row: HrCase): void {
    if (!row.caseId) {
      return;
    }
    this.hrService.sealCase(row.caseId).subscribe({
      next: () => {
        this.toast('success', 'HR case sealed.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to seal HR case.')
    });
  }

  selectCase(row: HrCase): void {
    this.selectedCase = row;
    if (!row.caseId) {
      return;
    }
    forkJoin({
      activities: this.hrService.caseActivities(row.caseId),
      attachments: this.hrService.caseAttachments(row.caseId),
      grants: this.canManageAccess ? this.hrService.caseAccessGrants(row.caseId) : of({ data: [] })
    }).subscribe({
      next: result => {
        this.activities = result.activities.data || [];
        this.attachments = result.attachments.data || [];
        this.accessGrants = result.grants.data || [];
        this.activityForm = this.newActivity(row.caseId);
        this.attachmentForm = this.newAttachment(row.caseId);
        this.grantForm = this.newGrant(row.caseId);
        this.disciplinaryForm = this.newDisciplinaryAction(row.caseId, row.employeeId);
      },
      error: () => {
        this.activities = [];
        this.attachments = [];
        this.accessGrants = [];
      }
    });
  }

  addActivity(): void {
    const caseId = this.selectedCase?.caseId;
    if (!caseId) {
      return;
    }
    this.hrService.addCaseActivity(caseId, this.activityForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Case activity added.');
        this.selectCase(this.selectedCase as HrCase);
      },
      error: () => this.toast('error', 'Unable to add case activity.')
    });
  }

  addAttachment(): void {
    const caseId = this.selectedCase?.caseId;
    if (!caseId) {
      return;
    }
    this.hrService.addCaseAttachment(caseId, this.attachmentForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Case attachment added.');
        this.selectCase(this.selectedCase as HrCase);
      },
      error: () => this.toast('error', 'Unable to add case attachment.')
    });
  }

  saveDisciplinaryAction(): void {
    this.hrService.saveDisciplinaryAction(this.disciplinaryForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Disciplinary action saved.');
        this.disciplinaryForm = this.newDisciplinaryAction(this.selectedCase?.caseId, this.selectedCase?.employeeId);
        this.load();
      },
      error: () => this.toast('error', 'Unable to save disciplinary action.')
    });
  }

  acknowledgeAction(row: HrDisciplinaryAction): void {
    if (!row.disciplinaryActionId) {
      return;
    }
    this.hrService.acknowledgeDisciplinaryAction(row.disciplinaryActionId, undefined, row.employeeId).subscribe({
      next: () => {
        this.toast('success', 'Disciplinary action acknowledged.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to acknowledge action.')
    });
  }

  grantAccess(): void {
    const caseId = this.selectedCase?.caseId;
    if (!caseId) {
      return;
    }
    this.hrService.grantCaseAccess(caseId, this.grantForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Case access granted.');
        this.selectCase(this.selectedCase as HrCase);
      },
      error: () => this.toast('error', 'Unable to grant case access.')
    });
  }

  revokeAccess(row: HrCaseAccessGrant): void {
    if (!row.caseAccessGrantId) {
      return;
    }
    this.hrService.revokeCaseAccess(row.caseAccessGrantId).subscribe({
      next: () => {
        this.toast('success', 'Case access revoked.');
        this.selectCase(this.selectedCase as HrCase);
      },
      error: () => this.toast('error', 'Unable to revoke case access.')
    });
  }

  openDialog(dialog: 'case' | 'activity' | 'disciplinary' | 'attachment' | 'access'): void {
    if (dialog === 'case') {
      this.caseForm = this.newCase();
    }
    this.activeDialog = dialog;
  }

  dialogVisible(dialog: 'case' | 'activity' | 'disciplinary' | 'attachment' | 'access'): boolean {
    return this.activeDialog === dialog;
  }

  setDialogVisible(dialog: 'case' | 'activity' | 'disciplinary' | 'attachment' | 'access', visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  severity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    switch (value) {
      case 'CLOSED':
      case 'RESOLVED':
      case 'ACKNOWLEDGED':
        return 'success';
      case 'HIGH':
      case 'CRITICAL':
        return 'danger';
      case 'OPEN':
      case 'IN_REVIEW':
      case 'PENDING_EMPLOYEE':
        return 'warn';
      default:
        return 'info';
    }
  }

  label(value?: string): string {
    return (value || '').toLowerCase().split('_').map(part => part ? part[0].toUpperCase() + part.slice(1) : part).join(' ');
  }

  private newCase(): HrCase {
    return { caseType: 'GENERAL', caseStatus: 'OPEN', severity: 'MEDIUM', confidentialityLevel: 'HR_RESTRICTED' };
  }

  private newActivity(caseId?: number): HrCaseActivity {
    return { caseId, activityType: 'NOTE', activityStatus: 'POSTED', visibility: 'HR_RESTRICTED' };
  }

  private newAttachment(caseId?: number): HrCaseAttachment {
    return { caseId, attachmentType: 'EVIDENCE', confidentialityLevel: 'HR_RESTRICTED' };
  }

  private newDisciplinaryAction(caseId?: number, employeeId?: number): HrDisciplinaryAction {
    return { caseId, employeeId, actionType: 'WRITTEN_WARNING', actionStatus: 'DRAFT', requiresAcknowledgement: true };
  }

  private newGrant(caseId?: number): HrCaseAccessGrant {
    return { caseId, accessLevel: 'VIEW' };
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: severity === 'error' ? 'HR Cases' : 'Saved', detail });
  }
}
